import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { householdsService } from '../services/households-service';
import { useAuthStore } from '../stores/auth-store';
import { MainLayout } from '../components/MainLayout';

interface HouseholdMember {
  id: string;
  user_id: string;
  email: string;
  role: string;
  is_accepted?: boolean;
  joined_at?: string;
}

interface PendingInvitation {
  id: string;
  household_id: string;
  household_name: string;
  invitee_email: string;
  inviter_email: string;
  role: string;
  invited_at: string;
  status: string;
}

export const HouseholdManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [myPendingInvitations, setMyPendingInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Member');
  const [inviting, setInviting] = useState(false);
  const [tab, setTab] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [membersRes, pendingRes, myPendingRes] = await Promise.all([
        householdsService.getMembers(),
        householdsService.getPendingInvitations(),
        householdsService.getMyPendingInvitations(),
      ]);

      if (membersRes.error) {
        setError(membersRes.error);
      } else {
        setMembers(membersRes.data || []);
      }

      if (pendingRes.error) {
        console.error('Failed to get pending invitations:', pendingRes.error);
      } else {
        setPendingInvitations(pendingRes.data || []);
      }

      if (myPendingRes.error) {
        console.error('Failed to get my pending invitations:', myPendingRes.error);
      } else {
        setMyPendingInvitations(myPendingRes.data || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load household data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      setError('Email is required');
      return;
    }

    setInviting(true);
    setError(null);
    try {
      const response = await householdsService.inviteUserByEmail(inviteEmail.trim(), inviteRole as any);
      if (response.error) {
        setError(response.error);
      } else {
        setInviteDialogOpen(false);
        setInviteEmail('');
        setInviteRole('Member');
        fetchData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleAcceptInvitation = async (householdId: string) => {
    setError(null);
    try {
      const response = await householdsService.acceptInvitation(householdId);
      if (response.error) {
        setError(response.error);
      } else {
        fetchData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    }
  };

  const handleRejectInvitation = async (householdId: string) => {
    setError(null);
    try {
      const response = await householdsService.rejectInvitation(householdId);
      if (response.error) {
        setError(response.error);
      } else {
        fetchData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reject invitation');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    setError(null);
    try {
      const response = await householdsService.removeMember(userId);
      if (response.error) {
        setError(response.error);
      } else {
        fetchData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const isOwnAccount = (userId: string) => {
    return user?.id === userId;
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Household Management
          </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteDialogOpen(true)}
        >
          Invite User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
          <Tab label={`Members (${members.length})`} />
          <Tab label={`Pending Invitations (${pendingInvitations.length})`} />
          <Tab label={`My Invitations (${myPendingInvitations.length})`} />
        </Tabs>

        <Divider />

        {tab === 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No members found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  members.map((member) => (
                    <TableRow
                      key={member.id}
                      sx={{
                        bgcolor: isOwnAccount(member.user_id) ? 'action.hover' : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {isOwnAccount(member.user_id) && (
                            <PersonIcon color="primary" fontSize="small" />
                          )}
                          <Typography
                            fontWeight={isOwnAccount(member.user_id) ? 'bold' : 'normal'}
                          >
                            {member.email}
                          </Typography>
                          {isOwnAccount(member.user_id) && (
                            <Chip label="You" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={member.role} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {member.is_accepted !== false && member.joined_at ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Active"
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Chip label="Pending" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!isOwnAccount(member.user_id) && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(member.user_id)}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            {pendingInvitations.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No pending invitations
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invitee Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Invited At</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingInvitations.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{inv.invitee_email}</TableCell>
                        <TableCell>
                          <Chip label={inv.role} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {new Date(inv.invited_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRejectInvitation(inv.household_id)}
                          >
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            {myPendingInvitations.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No pending invitations for you
              </Typography>
            ) : (
              myPendingInvitations.map((inv) => (
                <Card key={inv.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {inv.household_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Invited by: {inv.inviter_email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Role: {inv.role}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Invited on: {new Date(inv.invited_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleAcceptInvitation(inv.household_id)}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleRejectInvitation(inv.household_id)}
                        >
                          Reject
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Invite User to Household
          <IconButton
            aria-label="close"
            onClick={() => setInviteDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              helperText="Enter the exact email address of the user you want to invite"
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                <MenuItem value="Member">Member</MenuItem>
                <MenuItem value="Viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info">
              The user must have an account with this exact email. They will receive an invitation and must accept it to join.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviting || !inviteEmail.trim()}
          >
            {inviting ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </MainLayout>
  );
};

